import { useEffect, useMemo, useState } from "react";

type Heading = { id: string; text: string };

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

export function extractH2s(md: string): Heading[] {
  const out: Heading[] = [];
  const seen = new Set<string>();
  for (const line of md.split(/\r?\n/)) {
    const m = /^##\s+(.+?)\s*$/.exec(line);
    if (!m) continue;
    const text = m[1].replace(/\*\*/g, "").trim();
    let id = slugify(text) || `sektion-${out.length + 1}`;
    let i = 2;
    while (seen.has(id)) id = `${id}-${i++}`;
    seen.add(id);
    out.push({ id, text });
  }
  return out;
}

function useActiveHeading(ids: string[]) {
  const [active, setActive] = useState<string>(ids[0] ?? "");

  useEffect(() => {
    if (ids.length === 0) return;
    const observers: IntersectionObserver[] = [];
    const visible = new Map<string, number>();

    const handler = (entries: IntersectionObserverEntry[]) => {
      for (const e of entries) {
        visible.set(e.target.id, e.intersectionRatio);
      }
      let best = ids[0];
      let bestRatio = visible.get(best) ?? 0;
      for (const id of ids) {
        const ratio = visible.get(id) ?? 0;
        if (ratio > bestRatio) {
          best = id;
          bestRatio = ratio;
        }
      }
      if (bestRatio > 0) setActive(best);
    };

    for (const id of ids) {
      const el = document.getElementById(id);
      if (!el) continue;
      const obs = new IntersectionObserver(handler, {
        rootMargin: "-80px 0px -70% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
      obs.observe(el);
      observers.push(obs);
      visible.set(id, 0);
    }

    return () => observers.forEach((o) => o.disconnect());
  }, [ids.join(",")]);

  return active;
}

export function TableOfContents({
  markdown,
  sticky = false,
}: {
  markdown: string;
  sticky?: boolean;
}) {
  const items = useMemo(() => extractH2s(markdown), [markdown]);
  const active = useActiveHeading(items.map((h) => h.id));
  if (items.length < 3) return null;

  const list = (
    <ol className="space-y-2">
      {items.map((h) => {
        const isActive = active === h.id;
        return (
          <li key={h.id} className="flex gap-3 text-[15px] leading-snug">
            <a
              href={`#${h.id}`}
              className={`transition ${
                isActive
                  ? "font-medium text-accent"
                  : "text-foreground/70 hover:text-accent hover:underline"
              }`}
            >
              {h.text}
            </a>
          </li>
        );
      })}
    </ol>
  );

  if (sticky) {
    return (
      <nav
        aria-label="Innehållsförteckning"
        className="sticky top-24 hidden max-h-[calc(100vh-8rem)] overflow-y-auto rounded-3xl border border-border bg-card/70 p-6 xl:block"
      >
        <p className="eyebrow text-accent">I det här inlägget</p>
        {list}
      </nav>
    );
  }

  return (
    <nav
      aria-label="Innehållsförteckning"
      className="mt-10 rounded-3xl border border-border bg-card/60 p-6 md:p-7"
    >
      <p className="eyebrow text-accent">I det här inlägget</p>
      {list}
    </nav>
  );
}
